/**
 * useEmailHygieneReconciliationDetail
 *
 * DASH-EMAIL-013: real-data hook for a single Email Hygiene Reconciliation
 * detail (GET /api/email-hygiene/reconciliations/{id}).
 *
 * `notFound` is distinct from `error` — a 404 (the id no longer resolves,
 * e.g. a stale link after backend evidence-lifecycle changes) is an
 * honest, expected outcome, not a request failure (matching
 * getCreativeDetail/getBroadcastDetail's own null-on-404 convention in
 * emailMarketingApi.js, and EmailCreativeView's own
 * creativeDetailNotFound derivation).
 */
import { useState, useEffect, useCallback } from "react";
import {
  getEmailHygieneReconciliation,
  extractApiErrorMessage,
} from "../lib/emailHygieneReconciliationApi";

export function useEmailHygieneReconciliationDetail(reconciliationId) {
  const [loading, setLoading] = useState(!!reconciliationId);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!reconciliationId) {
      setDetail(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getEmailHygieneReconciliation(reconciliationId);
      setDetail(result);
    } catch (err) {
      setError(extractApiErrorMessage(err, "Failed to load reconciliation detail"));
    } finally {
      setLoading(false);
    }
  }, [reconciliationId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  return {
    loading,
    error,
    detail,
    notFound: !loading && !error && !!reconciliationId && detail === null,
    refresh: fetchDetail,
  };
}

export default useEmailHygieneReconciliationDetail;
